import * as React from 'react';
import { useCallback, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputLabel,
  OutlinedInput,
  Stack,
  Typography,
} from '@mui/material';
import Grid from '@mui/system/Unstable_Grid';
import { X as XIcon } from '@phosphor-icons/react';
import { useMutation } from '@tanstack/react-query';
import { Controller, useForm } from 'react-hook-form';
import InputMask from 'react-input-mask';
import { toast } from 'sonner';
import { z as zod } from 'zod';

import type { User } from '@/types/v2/user';
import axios from '@/lib/v2/axios';

interface EditMemberModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (user: User) => void;
  user: User;
  communityId: string;
}

const phoneNumberPattern = /^\(\d{3}\) \d{3}-\d{4}$/;

const schema = zod.object({
  first_name: zod.string().min(1, 'A first name is required'),
  last_name: zod.string().min(1, 'A last name is required'),
  phone: zod
    .string()
    .min(1, 'A phone number is required')
    .regex(phoneNumberPattern, 'Phone number must be in the format (123) 456-7890'),
});

type Values = zod.infer<typeof schema>;

export function EditMemberModal({
  open,
  onClose,
  onSuccess,
  communityId,
  user,
}: EditMemberModalProps): React.JSX.Element {
  console.log('user', user);
  if (!communityId) {
    return <div>No community ID</div>;
  }

  const defaultValues = {
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    phone: user?.phone || '',
  };

  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control,
  } = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues,
  });

  React.useEffect(() => {
    reset(defaultValues);
  }, [user, reset]);

  const handleClose = React.useCallback(() => {
    onClose();
    reset();
    setError(null);
  }, [onClose, reset, defaultValues]);

  const handleSuccess = React.useCallback(
    (user: User) => {
      onSuccess(user);
      handleClose();
    },
    [onSuccess, handleClose]
  );

  const createUserRequest = useMutation({
    mutationFn: async (data: Values) => {
      await axios.get('/sanctum/csrf-cookie');
      return axios.put(`/api/v2/communities/${communityId}/members/${user.id}`, data);
    },
  });

  const handleEditMember = useCallback(
    async (data: Values) => {
      console.log('data', data);
      try {
        const response = await createUserRequest.mutateAsync(data);
        handleSuccess(response?.data);
      } catch (error: any) {
        console.error(error);
        setError(error?.response?.data?.message || `An error occurred while editing the member`);
      }
    },
    [createUserRequest, handleSuccess]
  );

  return (
    <Dialog
      maxWidth="sm"
      onClose={onClose}
      open={open}
      sx={{
        '& .MuiDialog-container': { justifyContent: 'flex-end' },
        '& .MuiDialog-paper': { height: '100%', width: '100%', display: 'flex', flexDirection: 'column' },
      }}
    >
      <form
          onSubmit={handleSubmit(handleEditMember)}
        style={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}
      >
        <DialogContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Stack direction="row" sx={{ alignItems: 'center', flex: '0 0 auto', justifyContent: 'space-between' }}>
            <Typography variant="h6">Edit Member</Typography>
            <IconButton onClick={handleClose}>
              <XIcon />
            </IconButton>
          </Stack>
          <Stack direction="column" spacing={1}>
            <Alert severity="info">You can edit the member's name and phone number here.</Alert>
          </Stack>
          <Divider sx={{ mx: -3 }} />
          {error ? <Alert severity="error">{error}</Alert> : null}
          <Grid container spacing={2}>
            <Grid md={6} xs={12}>
              <FormControl error={Boolean(errors.first_name)} fullWidth>
                <InputLabel required>First Name</InputLabel>
                <OutlinedInput disabled={createUserRequest.isPending} {...register('first_name')} />
                {errors.first_name ? <FormHelperText>{errors.first_name.message}</FormHelperText> : null}
              </FormControl>
            </Grid>
            <Grid md={6} xs={12}>
              <FormControl error={Boolean(errors.last_name)} fullWidth>
                <InputLabel required>Last Name</InputLabel>
                <OutlinedInput disabled={createUserRequest.isPending} {...register('last_name')} />
                {errors.last_name ? <FormHelperText>{errors.last_name.message}</FormHelperText> : null}
              </FormControl>
            </Grid>
            <Grid md={6} xs={12}>
              <FormControl error={Boolean(errors.phone)} fullWidth>
                <InputLabel required>Phone</InputLabel>
                <OutlinedInput
                  disabled={createUserRequest.isPending}
                  {...register('phone')}
                  placeholder="(123) 456-7890"
                />
                {errors.phone ? <FormHelperText>{errors.phone.message}</FormHelperText> : null}
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ justifyContent: 'space-between' }}>
          <Button onClick={handleClose}>Cancel</Button>
          <LoadingButton
            disabled={createUserRequest.isPending}
            loading={createUserRequest.isPending}
            type="submit"
            variant="contained"
          >
            Save Changes
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
}
